/**
 * CouponListContainer
 * --
 */
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import CouponListPresenter from './CouponListPresenter';
import { useNavigate } from 'react-router-dom';
import MessageAlert, { calculateAge } from 'utils';
import Formatter from 'utils/Formatter';
import { useGetUsers } from 'hooks/UserHooks';
import { useGetGrades } from 'hooks/GradeHooks';
import {
  useGetShopInformations,
  useGetStores,
} from 'hooks/ShopInformationHooks';
import { useGetMenus } from 'hooks/MenuHooks';
import {
  useMapGetCoupons,
  useUpdateCoupon,
  useBulkUpdateMapMenuPolicy,
  useGetMapProductCoupons,
} from 'hooks/CouponHooks';
import {
  useBulkUpdateProductPolicy,
  useUpdateProductPolicy,
} from 'hooks/ProductPolicyHooks';
import { useBulkCreateCouponUser } from 'hooks/CouponUserHooks';
import { useGetProducts } from 'hooks/ProductHooks';
import { useGetAllCategories } from 'hooks/ProductCategoryHooks';

import { useUserStack } from 'store';
import { Spin } from 'antd';

import dayjs from 'dayjs';

const CouponListContainer = () => {
  /* ===== ROUTE ===== */
  const navigate = useNavigate();
  /* ===== PROPS ===== */
  /* ===== MUTATE ===== */
  const userInfo = useUserStack();
  const { updateBulkMapMenuPolicy } = useBulkUpdateMapMenuPolicy();
  const { bulkUpdateProductPolicy } = useBulkUpdateProductPolicy();
  const { updateProductPolicy } = useUpdateProductPolicy();
  const { updateCoupon } = useUpdateCoupon();
  const { bulkCreateCouponUser } = useBulkCreateCouponUser();
  /* ===== DATA ===== */
  // 쿠폰 전체 조회
  const { data: Coupons, refetch: menuCouponRefetch } = useMapGetCoupons();
  const { data: ProductCoupons, refetch: productCouponRefetch } =
    useGetMapProductCoupons(null, {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    });
  // 메뉴 전체 조회
  const { data: Menus } = useGetMenus({
    join_tables: '["product_category"]',
  });
  // 스토어 전체 조회
  const { data: Stores } = useGetStores({});
  // 유저 전체 조회
  const { data: Users } = useGetUsers({});
  // 등급 전체 조회
  const { data: Grades } = useGetGrades();
  // 상품 전체 조회
  const { data: Products } = useGetProducts(
    {
      join_tables: '["shop_information", "product_category"]',
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        // 렌탈 상품 필터
        const filter = data?.filter(
          (_item) =>
            _item?.type !== 'RENTAL' && _item?.application_status === 'APPROVE',
        );

        return filter;
      },
      initialData: [],
    },
  );
  // 카테고리 전체 조회
  const { data: Categories } = useGetAllCategories(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      return data;
    },
    initialData: [],
  });
  // 쇼핑몰 전체 조회
  const { data: Shops } = useGetShopInformations(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      const filter = data?.filter((_item) => _item?.type !== 'STORE');

      return filter;
    },
    initialData: [],
  });

  /* ===== STATE ===== */
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  // 정제된 데이터
  const [couponData, setCouponData] = React.useState([]);
  const [menuData, setMenuData] = React.useState([]);
  const [storeData, setStoreData] = React.useState([]);
  const [userData, setUserData] = React.useState([]);
  // 컨텐츠 데이터
  const [filteredCouponData, setFilteredCouponData] = useState([]);

  // 모달
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalType, setModalType] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  /**
   * 검색 관련 상태
   */
  const [searchParams, setSearchParams] = useState({
    searchField: 'coupon_name', // 'coupon_name' 또는 'coupon_code'
    searchKeyword: '',
    searchDatePreset: '전체', // '오늘', '1주일', '1개월', '3개월', '전체'
    searchCustomDateRange: { start: null, end: null },
    searchPaymentType: 'ALL', // 지급유형 (전체 선택시 'ALL')
    searchCouponType: null, // 쿠폰 타입 (전체 선택시 'ALL')
  });

  // 쿠폰 기본 정보
  const [selectCouponValue, setSelectCouponValue] = React.useState({
    coupon_name: '', // 쿠폰 이름
    coupon_code: '', // 쿠폰 코드
    coupon_content: {}, // 쿠폰 설명
    unavailable_store: '',
    note: '',
    benefit: '',
    standard: '',
    // coupon_issue_num: 0, // 발급 수량
    // minimum_price: 0, // 최소 사용 금액
    start_date: null, // 시작일
    end_date: null, // 종료일
    expiration_date: 30, // 만료 기간
    expiration_type: 'ROLLING', // 만료 타입 (ROLLING, FIXED)
    is_duplication: 'N', // 중복 사용 여부
    payment_type: 'MANUAL', // 지급 유형 (MANUAL, AUTOMATIC, DOWNLOAD, STAMP)
    coupon_type: 'MENU', // 쿠폰 타입 (MENU, FREQUENCY, MD, PRODUCT, DESERT, ALL)
    issue_event: null, // 발급 시점 (SIGNUP, FIRST_PURCHASE, REVIEW_POSTED, ORDER_COMPLETED, DELIVERY_COMPLETED)
  });
  const [selectPolicyValue, setSelectPolicyValue] = React.useState({
    // 정책 정보
    policy_type: 'COUPON', // 정책 타입 (고정)
    // discount_rate: 0, // 할인율
    // reserve_rate: 0, // 적립율
    discount_type: 'W', // 할인 타입 (원: W, 률: R)
    reserve_type: 'W', // 적립 타입 (원: W, 률: R)
    commission_rate: 0, // 수수료율 (0 ~ 100)
    share_ratio: 0, // 분담 비율 (0 ~ 100, 기본값: 100)
    is_show: 'Y', // 표시 여부
  });
  const [storeValue, setStoreValue] = React.useState([]);
  const [menuValue, setMenuValue] = React.useState([]);
  const [userValue, setUserValue] = React.useState([]);
  const [sideSearchParams, setSideSearchParams] = React.useState({
    process_coupon_name: null,
    process_coupon_code: null,
    expired_coupon_name: null,
    expired_coupon_code: null,
  });
  // 전체 매장 선택
  const [isCheckedStoreAll, setIsCheckedStoreAll] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'TOGGLE':
          return !state;
        case 'RESET':
          return false;
        default:
          return state;
      }
    },
    false,
  );
  // 전체 메뉴 선택
  const [isCheckedMenuAll, setIsCheckedMenuAll] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'TOGGLE':
          return !state;
        case 'RESET':
          return false;
        default:
          return state;
      }
    },
    false,
  );
  // 전체 유저 선택
  const [isCheckedUserAll, setIsCheckedUserAll] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'TOGGLE':
          return !state;
        case 'RESET':
          return false;
        default:
          return state;
      }
    },
    false,
  );
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 검색 결과 필터링 핸들러
   */
  const handleSearch = () => {
    setIsLoading(true);

    let result = couponData || [];
    const {
      searchField,
      searchKeyword,
      searchDatePreset,
      searchCustomDateRange,
      searchPaymentType,
      searchCouponType,
    } = searchParams;

    if (searchKeyword.trim() !== '') {
      result = result.filter(
        (item) =>
          item[searchField] &&
          item[searchField].toLowerCase().includes(searchKeyword.toLowerCase()),
      );
    }
    if (searchPaymentType !== 'ALL') {
      result = result.filter((item) => item.payment_type === searchPaymentType);
    }
    if (searchCouponType) {
      result = result.filter((item) => item.coupon_type === searchCouponType);
    }
    // 날짜 필터링: 커스텀 날짜 우선, 없으면 preset 사용
    if (searchCustomDateRange.start && searchCustomDateRange.end) {
      const startUnix = Formatter.dateToUnix(searchCustomDateRange.start);
      const endUnix = Formatter.dateToUnix(searchCustomDateRange.end);
      result = result.filter(
        (item) => item.start_date >= startUnix && item.end_date <= endUnix,
      );
    } else if (searchDatePreset !== '전체') {
      const today = new Date();
      const todayUnix = Formatter.dateToUnix(today);
      switch (searchDatePreset) {
        case '오늘':
          result = result.filter(
            (item) =>
              item.start_date <= todayUnix && item.end_date >= todayUnix,
          );
          break;
        case '1주일': {
          const weekLater = Formatter.dateToUnix(
            new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000),
          );
          result = result.filter(
            (item) =>
              item.start_date >= todayUnix && item.start_date <= weekLater,
          );
          break;
        }
        case '1개월': {
          const monthLater = Formatter.dateToUnix(
            new Date(new Date().setMonth(new Date().getMonth() + 1)),
          );
          result = result.filter(
            (item) =>
              item.start_date >= todayUnix && item.start_date <= monthLater,
          );
          break;
        }
        case '3개월': {
          const threeMonthsLater = Formatter.dateToUnix(
            new Date(new Date().setMonth(new Date().getMonth() + 3)),
          );
          result = result.filter(
            (item) =>
              item.start_date >= todayUnix &&
              item.start_date <= threeMonthsLater,
          );
          break;
        }
        default:
          break;
      }
    }
    setTimeout(() => {
      setFilteredCouponData(result);
      setCurrentPage(1);
      setIsLoading(false);
    }, 1000);
  };

  /**
   * 검색 초기화 핸들러
   */
  const handleReset = () => {
    setIsLoading(true);

    setTimeout(() => {
      setSearchParams({
        searchField: 'coupon_name',
        searchKeyword: '',
        searchDatePreset: '전체',
        searchCustomDateRange: { start: null, end: null },
        searchPaymentType: 'ALL',
        searchCouponType: null,
      });
      setFilteredCouponData(couponData);

      setIsLoading(false);
    }, 500);
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = useCallback((t, v, o, callback = null) => {
    callback((prev) => {
      if (prev[t] === v) return prev;

      const newData = { ...prev, [t]: v };

      return newData;
    });
  }, []);

  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    // setModalData((prev) => ({
    //   ...prev,
    //   [type]: data,
    // }));
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 체크박스 핸들러
   * --
   * @param { boolean } c
   * @param { object | array } d
   * @param { string } t
   */
  const handleStoreChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setStoreValue(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setStoreValue((prev) => [...prev, d]);
      } else {
        setStoreValue((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };
  const handleMenuChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setMenuValue(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setMenuValue((prev) => [...prev, d]);
      } else {
        setMenuValue((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };
  const handleUserChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setUserValue(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setUserValue((prev) => [...prev, d]);
      } else {
        setUserValue((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };

  /**
   * 데이터 생성
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE', v = 1, callback) => {
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();
    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      MENU: '메뉴',
      PRODUCT: '상품',
      COUPON: '쿠폰',
      COUPONUSER: '쿠폰함 발급',
      STORE: '매장',
      POLICY: '혜택',
      CREATE: '생성',
      UPDATE: '수정',
    };

    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = handleValidateCheck(type, v);

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      MENU: {
        UPDATE: updateBulkMapMenuPolicy,
      },
      PRODUCT: {
        UPDATE: bulkUpdateProductPolicy,
      },
      COUPON: {
        UPDATE: updateCoupon,
      },
      POLICY: {
        UPDATE: updateProductPolicy,
      },
      STORE: {
        UPDATE: updateCoupon,
      },
      COUPONUSER: {
        CREATE: bulkCreateCouponUser,
      },
    };

    let success = false;
    try {
      // 로딩 처리
      setIsLoading(true);
      // 데이터 생성/수정
      const { status } = await submitFunc[type][submit](data);

      if (status === 200) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`,
        );
        setSelectCouponValue({});
        setSelectPolicyValue({});
        setMenuValue([]);
        setStoreValue([]);
        setUserValue([]);
        setIsOpened(false);

        success = true;

        callback && callback();

        return;
      }

      if (type === 'COUPONUSER') {
        MessageAlert.warning(
          '쿠폰 발급이 중복,발급 수량을 초과 등의 이유로 발급할수 없습니다.',
        );
      } else {
        MessageAlert.warning(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`,
        );
      }
    } catch (err) {
      if (type === 'COUPONUSER') {
        MessageAlert.warning(
          '쿠폰 발급이 중복,발급 수량을 초과 등의 이유로 발급할수 없습니다.',
        );
      } else {
        MessageAlert.error(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`,
        );
        console.error(`[CREATE ${type}] ERROR : `, err);
        throw err;
      }
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);

        if (success) {
          menuCouponRefetch();
          productCouponRefetch();
        }
      }, 1000);
    }
  };

  /**
   * 유효성 검사
   * --
   */
  const handleValidateCheck = (type, value) => {
    if (type === 'COUPON') {
      // NOTE: 쿠폰 기본 정보 유효성 검사
      const {
        coupon_name,
        coupon_issue_num,
        minimum_price,
        voucher_type,
        payment_type,
        issue_event,
      } = selectCouponValue;

      // 쿠폰명 입력 여부 체크
      if (!coupon_name) {
        MessageAlert.warning('쿠폰명을 입력해주세요.');
        return false;
      }
      // 발급수량 입력 여부 체크
      if (coupon_issue_num !== 0 && !coupon_issue_num) {
        MessageAlert.warning('발급수량을 입력해주세요.');
        return false;
      }
      // minimum_price 입력 여부 체크
      if (minimum_price !== 0 && !minimum_price) {
        MessageAlert.warning('최소할인 금액을 입력해주세요.');
        return false;
      }
      // voucher_type 입력 여부 체크
      if (!voucher_type) {
        MessageAlert.warning('바우처타입을 선택해주세요.');
        return false;
      }
      // 발급시점 입력 여부 체크
      if (payment_type === 'AUTOMATIC') {
        if (!issue_event) {
          MessageAlert.warning('발급시점을 입력해주세요.');
          return false;
        }
      }

      return {
        ...selectCouponValue,
        coupon_content: {
          unavailable_store: selectCouponValue?.unavailable_store, //대표자 명
          note: selectCouponValue?.note, // 매장 연락처
          benefit: selectCouponValue?.benefit, // 사업자등록번호
          standard: selectCouponValue?.standard, // 영업시간
        },
        start_date: selectCouponValue?.start_date
          ? dayjs(selectCouponValue?.start_date).startOf('day').unix()
          : null,
        end_date: selectCouponValue?.end_date
          ? dayjs(selectCouponValue?.end_date).endOf('day').unix()
          : null,
      };
    }

    if (type === 'POLICY') {
      // NOTE: 정책 정보 유효성 검사
      const {
        // 정책 정보
        discount_rate,
        reserve_rate,
        max_discount_price,
      } = selectPolicyValue;

      // 할인율 입력 여부 체크
      if (selectCouponValue?.voucher_type !== 'EXCHANGE') {
        if (discount_rate !== 0 && !discount_rate) {
          MessageAlert.warning('할인율/금액을 입력해주세요.');
          return false;
        }
      }
      // 적립율 입력 여부 체크
      if (reserve_rate !== 0 && !reserve_rate) {
        MessageAlert.warning('적립율/금액을 입력해주세요.');
        return false;
      }
      // 최대 할인 금액 입력 여부 체크
      if (max_discount_price !== 0 && !max_discount_price) {
        MessageAlert.warning('최대 할인 금액을 입력해주세요.');
        return false;
      }

      return {
        ...selectPolicyValue,
        discount_rate:
          selectCouponValue?.voucher_type === 'EXCHANGE'
            ? 0
            : selectPolicyValue?.discount_rate,
        discount_type:
          selectCouponValue?.voucher_type === 'PERCENT_DISCOUNT' ? 'R' : 'W',
      };
    }

    if (type === 'STORE') {
      const disabled_shop_list = storeValue?.map((item) => ({
        shop_information_id: item.shop_information_id,
      }));
      return {
        coupon_id: selectCouponValue?.coupon_id,
        disabled_shop: disabled_shop_list ? disabled_shop_list : null,
      };
    }

    if (type === 'MENU') {
      // map_menu_policy 데이터를 menu_id 기준으로 Map 생성
      const mapMenuPolicyMap = new Map(
        selectCouponValue?.map_menu_policy?.map((item) => [item.menu_id, item]),
      );

      // menuValue 기준으로 데이터 생성
      const menuValueResult = menuValue.map((menu) => {
        const policy = mapMenuPolicyMap.get(menu.menu_id);
        return {
          map_menu_policy_id: policy?.map_menu_policy_id || null, // 정책이 없으면 null
          menu_id: menu.menu_id,
          product_policy_id: selectCouponValue?.product_policy_id, // 정책이 없으면 null
          policy_status: policy?.policy_status || 'POSSIBLE', // 기본값 설정
          is_enable: 'Y', // menuValue에 있는 데이터는 항상 'Y'
        };
      });

      // map_menu_policy에 존재하지만 menuValue에 없는 항목 처리
      const removedPolicies = Array.from(mapMenuPolicyMap.values())
        .filter(
          (policy) =>
            !menuValue.some((menu) => menu.menu_id === policy.menu_id),
        )
        .map((policy) => ({
          map_menu_policy_id: policy.map_menu_policy_id,
          menu_id: policy.menu_id,
          product_policy_id: selectCouponValue?.product_policy_id,
          policy_status: policy.policy_status,
          created_at: policy.created_at,
          is_enable: 'N', // menuValue에 없으므로 'N'
        }));

      // 두 결과를 병합하여 반환
      return [...menuValueResult, ...removedPolicies];
    }

    if (type === 'PRODUCT') {
      const existing = selectCouponValue?.map_product_policy ?? [];

      // 선택된 product_id 집합
      const selectedSet = new Set((menuValue ?? []).map((m) => m.product_id));

      // product_id -> 기존 rows[]
      const byProduct = new Map();
      for (const row of existing) {
        if (!byProduct.has(row.product_id)) byProduct.set(row.product_id, []);
        byProduct.get(row.product_id).push(row);
      }

      const result = [];

      // 1) 기존 row들은 전부 업데이트 대상으로 만든다 (중복 row 포함)
      for (const [product_id, rows] of byProduct.entries()) {
        // 대표 1개 선택 룰: map_product_policy_id 가장 큰 것
        const sorted = [...rows].sort(
          (a, b) =>
            (b.map_product_policy_id ?? 0) - (a.map_product_policy_id ?? 0),
        );
        const winnerId = sorted[0]?.map_product_policy_id;

        const isSelected = selectedSet.has(product_id);

        for (const row of rows) {
          result.push({
            map_product_policy_id: row.map_product_policy_id,
            product_id: row.product_id,
            product_policy_id: selectCouponValue?.product_policy_id ?? null,
            policy_status: row.policy_status ?? 'POSSIBLE',
            created_at: row.created_at,
            // ✅ 선택된 상품이면 winner 1개만 Y, 나머지 N
            // ✅ 선택 안된 상품이면 전부 N
            is_enable:
              isSelected && row.map_product_policy_id === winnerId ? 'Y' : 'N',
          });
        }
      }

      // 2) 신규 선택(product_id는 선택됐는데 기존 row가 없음) => 1개만 생성(Y)
      const existingProductIdSet = new Set(byProduct.keys());
      for (const menu of menuValue ?? []) {
        if (!existingProductIdSet.has(menu.product_id)) {
          result.push({
            map_product_policy_id: null,
            product_id: menu.product_id,
            product_policy_id: selectCouponValue?.product_policy_id ?? null,
            policy_status: 'POSSIBLE',
            is_enable: 'Y',
          });
        }
      }

      return result;
    }
    // if (type === 'PRODUCT') {
    //   // 1) product_id -> policies[] 로 그룹핑
    //   const mapMenuPolicyMap = new Map();
    //   (selectCouponValue?.map_product_policy ?? []).forEach((item) => {
    //     const key = item.product_id;
    //     if (!mapMenuPolicyMap.has(key)) mapMenuPolicyMap.set(key, []);
    //     mapMenuPolicyMap.get(key).push(item);
    //   });

    //   // 2) menuValue의 product_id 집합(빠른 조회용)
    //   const enabledProductIdSet = new Set(menuValue.map((m) => m.product_id));

    //   // 3) 기존 map_product_policy는 모두 기준 데이터로 펼치고
    //   //    menuValue에 있으면 Y, 없으면 N으로 업데이트 payload 생성 (누락 없음)
    //   const updatedFromExisting = [];
    //   for (const policies of mapMenuPolicyMap.values()) {
    //     for (const policy of policies) {
    //       const isEnabled = enabledProductIdSet.has(policy.product_id);
    //       updatedFromExisting.push({
    //         map_product_policy_id: policy.map_product_policy_id,
    //         product_id: policy.product_id,
    //         product_policy_id: selectCouponValue?.product_policy_id ?? null,
    //         policy_status: policy.policy_status ?? 'POSSIBLE',
    //         created_at: policy.created_at,
    //         is_enable: isEnabled ? 'Y' : 'N',
    //       });
    //     }
    //   }

    //   // 4) menuValue에는 있는데 기존 map_product_policy에 없는(product_id 신규 추가) 항목 처리
    //   //    (여기서도 product_id가 이미 존재하면 새로 만들지 않음)
    //   const existingProductIdSet = new Set(mapMenuPolicyMap.keys());
    //   const createdNew = menuValue
    //     .filter((menu) => !existingProductIdSet.has(menu.product_id))
    //     .map((menu) => ({
    //       map_product_policy_id: null,
    //       product_id: menu.product_id,
    //       product_policy_id: selectCouponValue?.product_policy_id ?? null,
    //       policy_status: 'POSSIBLE',
    //       is_enable: 'Y',
    //     }));

    //   return [...updatedFromExisting, ...createdNew];
    // }

    if (type === 'COUPONUSER') {
      if (userValue?.length === 0) {
        MessageAlert.warning('회원을 선택해주세요.');
        return false;
      }

      if (!value) {
        MessageAlert.warning('발급수량을 입력해주세요.');
        return false;
      }

      return {
        user_ids: userValue.map((user) => ({
          user_id: user.user_id, // 사용자 ID만 포함
        })),
        coupon_id: selectCouponValue?.coupon_id, // 쿠폰 ID 추가
        per_user_limit: value ?? 1,
      };
    }
  };

  /**
   * 상품 선택 핸들러
   * --
   * @param {string} type
   * @param {object | array} obj
   */
  const handleSelect = (
    type,
    obj,
    arr = menuValue,
    callback = setMenuValue,
  ) => {
    const selectType = type.toLocaleUpperCase();

    switch (selectType) {
      case 'CHECKED': // 개별 선택
        callback((prev) => {
          return [...prev, obj];
        });
        return;
      case 'UNCHECKED': // 개별 선택 해제
        callback((prev) =>
          prev.filter((select) => {
            return select.key !== obj.key;
          }),
        );
        return;
      case 'ALL-CHECKED': // 노출된 목록 전체 선택
        callback(obj);
        return;
      case 'ALL-UNCHECKED': // 노출된 목록 전체 선택 해제
        const removeList = arr.filter((user) =>
          obj.some((ob) => ob.key === user.key),
        );

        callback(removeList);

        return;
      case 'ALL': // 검색된 회원 전체 선택/해제
        const isAll = arr.length === obj.length;

        if (!isAll) {
          callback(obj);
        } else {
          callback([]);
        }
        return;
      default:
        return;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 쿠폰 데이터 정제
   * --
   */
  useEffect(() => {
    const concatData = Coupons?.data?.concat(ProductCoupons);

    const newData = concatData
      // ?.filter((item) => item?.is_show === 'Y')
      ?.map((item) => ({
        ...item,
        discount: {
          discount_rate: item.discount_rate,
          discount_type: item.discount_type,
        },
        reserve: {
          reserve_rate: item.reserve_rate,
          reserve_type: item.reserve_type,
        },
      }));

    setCouponData(newData);
    setFilteredCouponData(newData);
  }, [Coupons, ProductCoupons]);

  /**
   * 매장 데이터 정제
   * --
   */
  useEffect(() => {
    const newData = Stores?.data
      ?.filter(
        (item) =>
          item?.application_status === 'APPROVE' && item?.type === 'STORE',
      )
      .map((item) => ({
        ...item,
        key: item.shop_information_id,
        business_num: item.store_information.business_num,
      }));

    setStoreData(newData);
  }, [Stores]);

  /**
   * 메뉴 데이터 정제
   * --
   */
  useEffect(() => {
    let newData = [];

    if (modalType === 'menu_edit') {
      newData = Menus?.data
        ?.filter((item) =>
          item?.product_category?.product_category_name &&
          selectCouponValue?.coupon_type === 'MENU'
            ? item?.type === 'GENERAL'
            : item?.type === selectCouponValue?.coupon_type,
        ) // product_category_name이 있는 데이터만 필터링
        .map((item) => ({
          ...item,
          key: item.menu_id,
          product_category_name: item.product_category.product_category_name, // product_category_name을 상위로 이동
          product_category: undefined, // product_category 제거
        }));
    } else {
      newData = Products?.map((_item) => ({
        ..._item,
        key: _item?.product_id,
        product_category_name: _item?.product_category?.product_category_name,
        product_category_id: _item?.product_category?.product_category_id,
        shop_information_name: _item?.shop_information?.shop_information_name,
      }));
    }

    setMenuData(newData);
  }, [Menus, Products, modalType]);

  /**
   * 회원 데이터 정제
   * --
   */
  useEffect(() => {
    const newData = Users?.data?.map((user) => ({
      ...user,
      key: user.user_id,
      grade: Grades?.data?.find((grade) => grade?.grade_id === user?.grade_id)
        ?.grade_name,
      age: calculateAge(Formatter.unixToDate(user?.user_birthday))?.age,
    }));

    setUserData(newData);
  }, [Users]);

  /* ===== RENDER ===== */
  return (
    <>
      <Spin spinning={isLoading}>
        <CouponListPresenter
          auth={userInfo?.admin_privilege}
          couponData={couponData}
          filteredCouponData={filteredCouponData}
          storeData={storeData}
          menuData={menuData}
          userData={userData}
          selectCouponValue={selectCouponValue}
          selectPolicyValue={selectPolicyValue}
          storeValue={storeValue}
          menuValue={menuValue}
          userValue={userValue}
          isOpened={isOpened}
          modalType={modalType}
          // modalData={modalData}
          setSelectCouponValue={setSelectCouponValue}
          setSelectPolicyValue={setSelectPolicyValue}
          setStoreValue={setStoreValue}
          setMenuValue={setMenuValue}
          setUserValue={setUserValue}
          onStoreChecked={handleStoreChecked}
          isCheckedStoreAll={isCheckedStoreAll}
          onOpen={handleModal}
          onStoreCheckedAll={setIsCheckedStoreAll}
          onMenuChecked={handleMenuChecked}
          isCheckedMenuAll={isCheckedMenuAll}
          onMenuCheckedAll={setIsCheckedMenuAll}
          onUserChecked={handleUserChecked}
          isCheckedUserAll={isCheckedUserAll}
          onUserCheckedAll={setIsCheckedUserAll}
          onChange={handleChange}
          onSubmit={handleSubmit}
          /* --- 검색 관련 props 추가 --- */
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          sideSearchParams={sideSearchParams}
          setSideSearchParams={setSideSearchParams}
          handleSearch={handleSearch}
          handleReset={handleReset}
          navigate={navigate}
          // NOTE-
          onSelect={handleSelect}
          categories={Categories}
          shops={Shops}
          grades={Grades?.data}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </Spin>
    </>
  );
};

export default CouponListContainer;
